<template>
    <v-container>
        <v-row>
            <v-col>
                <v-card class="mb-6">
                    <v-card-title class="text-uppercase justify-center">{{ metaData.header }}</v-card-title>
                    <v-card-actions class="flex-column flex-sm-row justify-center justify-sm-end" style="gap: 8px">
                        <span class="d-none">
                            <v-btn color="teal" class="white--text" @click="openAddCategoryDialog">
                                {{ $t('addCategory') }}
                            </v-btn>
                        </span>
                        <span v-if="!showDeletedCategories">
                            <category-status-dropdown :selected-items="selectedItems.map(item => item.id)"
                                @update="onActionSuccess" @error="onActionError"></category-status-dropdown>
                        </span>
                        <span v-if="!showDeletedCategories">
                            <category-code-dropdown :selected-items="selectedItems.map(item => item.id)"
                                @update="onActionSuccess" @error="onActionError"></category-code-dropdown>
                        </span>
                        <span v-if="!showDeletedCategories">
                            <categories-bulk-delete :selected-items="selectedItems" @update="onActionSuccess"
                                @error="onActionError" @restricted="onBulkDeleteRestricted"></categories-bulk-delete>
                        </span>
                    </v-card-actions>
                </v-card>
                <categories-filters @filter-change="filterChange" ref="attributeFilters"></categories-filters>
                <v-card class="mt-6">
                    <categories-table :filters="filters" @load-error="onDataLoadError" @update="onCategoryUpdate"
                        @delete="onCategoryDelete"
                        @restore="onCategoryRestore" @restricted="onCategoryDeleteRestricted"></categories-table>
                </v-card>
            </v-col>
        </v-row>
        <category-dialog :is-open="openCategoryDialog" :dialog-type="categoryDialogType"
            :initial-category="categoryToUpdate" @close="onCategoryDialogClose" @submitted="onActionSuccess"
            @failed="onActionError"></category-dialog>
        <v-snackbar v-model="snackOpen" :color="snackColor" top>
            {{ snackText }}
            <template v-slot:action="{ attrs }">
                <v-btn text icon v-bind="attrs" @click="resetSnack">
                    <v-icon color="grey">close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>
        <load-overlay :overlay="overlay" :z-index="10"></load-overlay>
    </v-container>
</template>

<script>
import LoadOverlay from "../../../components/overlays/LoadOverlay";
import { CategoriesTable } from "../../../components/data-tables";
import { CategoriesFilters } from "../../../components/filters";
import { CategoryStatusDropdown, CategoryCodeDropdown, CategoriesBulkDelete } from "../../../components/dropdowns";
import { CategoryDialog, EnumAttributeValueDialog, EnumAttributeValueFullscreenDialog } from "../../../components/modals"
import { MixinTableLoadSnack } from '../../../mixins'
import { CategoriesService } from "../../../services";

export default {
    name: "ReviewCategories",
    components: {
        CategoriesTable,
        CategoriesFilters,
        LoadOverlay,
        CategoryDialog,
        EnumAttributeValueDialog,
        EnumAttributeValueFullscreenDialog,
        CategoryStatusDropdown,
        CategoryCodeDropdown,
        CategoriesBulkDelete
    },
    mixins: [MixinTableLoadSnack],
    data() {
        return {
            metaUrl: '/categories',
            overlay: false,
            filters: {},
            openCategoryDialog: false,
            categoryDialogType: 'add',
            categoryToUpdate: null,
        }
    },
    computed: {
        selectedItems() {
            return this.$store.getters.selectedCategories;
        },
        showDeletedCategories() {
            return Boolean(this.filters.category_deleted);
        }
    },
    methods: {
        filterChange(filters) {
            if (Boolean(this.filters.category_deleted) !== Boolean(filters.category_deleted)) {
                this.$store.commit('setSelectedCategories', []);
            }

            this.filters = filters;
        },
        openAddCategoryDialog() {
            this.openCategoryDialog = true;
            this.categoryDialogType = 'add';
        },
        openEditCategoryDialog() {
            this.openCategoryDialog = true;
            this.categoryDialogType = 'edit';
        },

        /**
         * @param {{ type: 'status' | 'code' | 'categoryAdd' | 'categoryUpdate' | 'categoryDelete' | 'categoryBulkDelete' | 'categoryRestore', data: any }} action
         */
        onActionSuccess(action) {
            const handleAction = () => {
                this.setSuccessSnack();
                this.openSnack();
                this.onDataUpdated();
            }

            switch (action.type) {
                case 'status':
                    this.setSnackText(this.$t('statusUpdateSuccess'));
                    handleAction();
                    break;
                case 'code':
                    this.setSnackText(this.$t('codeUpdateSuccess'));
                    handleAction();
                    break;
                case 'categoryAdd':
                    this.onFormSubmitted();
                    this.setSnackText(this.$t('categoryAddSuccess'));
                    handleAction();
                    break;
                case 'categoryUpdate':
                    this.onFormSubmitted();
                    this.setSnackText(this.$t('categoryUpdateSuccess'));
                    handleAction();
                    break;
                case 'categoryDelete':
                    this.setSnackText(this.$t('categoryDeleted'));
                    handleAction();
                    break;
                case 'categoryBulkDelete':
                    this.$store.commit('setSelectedCategories', []);
                    this.$store.commit('setUpdateCategoriesFilters', true);
                    this.setSnackText(this.$t('categoryBulkDeleteSuccess'));
                    handleAction();
                    break;
                case 'categoryRestore':
                    this.$store.commit('setUpdateCategoriesFilters', true);
                    this.setSnackText(this.$t('categoryRestoreSuccess'));
                    handleAction();
                    break;
                default:
                    console.warn(`Unknown action type: ${action.type}`);
            }
        },

        /**
         * @param {{ type: 'status' | 'code' | 'categoryAdd' | 'categoryUpdate' | 'categoryDelete' | 'categoryBulkDelete' | 'categoryRestore', data: any }} action
         */
        onActionError(action) {
            const handleAction = () => {
                this.setErrorSnack();
                this.openSnack();
            }

            switch (action.type) {
                case 'status':
                    this.setSnackText(this.$t('statusUpdateError'));
                    handleAction();
                    break;
                case 'code':
                    this.setSnackText(this.$t('codeUpdateError'));
                    handleAction();
                    break;
                case 'categoryAdd':
                    this.setSnackText(this.$t('categoryAddError'));
                    handleAction();
                    break;
                case 'categoryUpdate':
                    this.setSnackText(this.$t('categoryUpdateError'));
                    handleAction();
                    break;
                case 'categoryDelete':
                    this.setSnackText(this.$t('categoryDeleteError'));
                    handleAction();
                    break;
                case 'categoryBulkDelete':
                    this.setSnackText(this.$t('categoryBulkDeleteError'));
                    handleAction();
                    break;
                case 'categoryRestore':
                    this.setSnackText(this.$t('categoryRestoreError'));
                    handleAction();
                    break;
                default:
                    console.warn(`Unknown action type: ${action.type}`);
            }
        },

        onDataUpdated(data) {
            this.$store.commit('setUpdateCategoriesTable', true);
        },

        onBulkDeleteRestricted() {
            this.setWarningSnack();
            this.setSnackText(this.$t('categoryBulkDeleteRestricted'));
            this.openSnack();
        },

        onCategoryDeleteRestricted() {
            this.setWarningSnack();
            this.setSnackText(this.$t('categoryDeleteRestricted'));
            this.openSnack();
        },

        onFormSubmitted(formData) {
            this.$store.commit('setUpdateCategoriesTable', true);
            this.$store.commit('setUpdateCategoriesFilters', true);
            this.openCategoryDialog = false;
        },

        onCategoryDialogClose() {
            this.openCategoryDialog = false;
            this.categoryToUpdate = null;
            this.categoryDialogType = 'add';
        },

        onCategoryUpdate(id) {
            this.overlay = true;
            CategoriesService.getCategoryDetails(id)
                .then(response => {
                    this.categoryToUpdate = response.data;
                    this.categoryDialogType = 'edit';
                    this.openCategoryDialog = true;
                })
                .catch(error => {
                    console.error('Error fetching category details:', error);
                })
                .finally(() => {
                    this.overlay = false;
                });

        },
        onCategoryDelete(id) {
            this.overlay = true;
            CategoriesService.delete(id)
                .then(() => {
                    this.$store.commit('setUpdateCategoriesTable', true);
                    this.$store.commit('setUpdateCategoriesFilters', true);
                    this.onActionSuccess({ type: 'categoryDelete', data: id });
                })
                .catch(error => {
                    console.error('Error deleting category:', error);
                    this.overlay = false;
                    this.onActionError({ type: 'categoryDelete', data: error });
                });

        },
        onCategoryRestore(id) {
            this.overlay = true;
            CategoriesService.restore(id)
                .then(() => {
                    this.onActionSuccess({ type: 'categoryRestore', data: id });
                })
                .catch(error => {
                    console.error('Error restoring category:', error);
                    this.overlay = false;
                    this.onActionError({ type: 'categoryRestore', data: error });
                });
        }
    },
    created() {
        CategoriesService.setAuth(this.$store.getters.token);
    }
}
</script>
